import * as React from 'react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ApiError, getJSON, patchJSON } from '@/lib/api'

export type BulkChapterAction = 'add' | 'remove'
export interface BulkChapterSelection {
  contact_ids?: number[]
  matching?: Record<string, string | boolean>
}
export interface BulkChapterChangeResponse {
  bulk_chapter_change: { selected_count: number; changed_count: number }
}

export function BulkChapterNotice({
  result,
}: {
  result: BulkChapterChangeResponse['bulk_chapter_change'] | null
}) {
  if (!result) return null
  return (
    <Alert className="m-4">
      Chapter change complete: {result.changed_count} of {result.selected_count} selected Contacts
      changed.
    </Alert>
  )
}

export function ContactsBulkChapterDialog({
  open,
  onOpenChange,
  action,
  selection,
  selectedCount,
  onApplied,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  action: BulkChapterAction | null
  selection: BulkChapterSelection
  selectedCount: number
  onApplied: (response: BulkChapterChangeResponse) => void
}) {
  const requestedAction = action ?? 'add'
  const [chapterId, setChapterId] = React.useState('')
  const [chapters, setChapters] = React.useState<Array<{ id: number; name: string }>>([])
  const [preview, setPreview] = React.useState<BulkChapterChangeResponse | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [pending, setPending] = React.useState(false)
  React.useEffect(() => {
    if (!open) return
    setChapterId('')
    setPreview(null)
    setError(null)
    void getJSON<{ chapters: Array<{ id: number; name: string }> }>('/api/v1/chapters')
      .then((response) => setChapters(response.chapters))
      .catch(() => setError('Chapters unavailable.'))
  }, [open])
  async function submit(confirm: boolean) {
    setPending(true)
    setError(null)
    try {
      const response = confirm
        ? await request(chapterId, requestedAction, selection, true)
        : await previewRequest(chapterId, requestedAction, selection)
      if (confirm) {
        onApplied(response)
        onOpenChange(false)
      } else setPreview(response)
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Chapter change failed.')
    } finally {
      setPending(false)
    }
  }
  const verb = requestedAction === 'add' ? 'Add to' : 'Remove from'
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{verb} Chapter</DialogTitle>
          <DialogDescription>
            Choose a Chapter, then confirm the change for {selectedCount} selected Contacts.
          </DialogDescription>
        </DialogHeader>
        {error && <Alert>{error}</Alert>}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="bulk-contact-chapter">Chapter</Label>
          <Select
            value={chapterId}
            onValueChange={(value) => {
              setChapterId(value)
              setPreview(null)
            }}
          >
            <SelectTrigger id="bulk-contact-chapter">
              <SelectValue placeholder="Select a Chapter" />
            </SelectTrigger>
            <SelectContent>
              {chapters.map((chapter) => (
                <SelectItem key={chapter.id} value={String(chapter.id)}>
                  {chapter.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {preview && (
          <div className="rounded-md border border-border bg-accent/40 px-3 py-2 text-body-sm text-muted-foreground">
            {preview.bulk_chapter_change.selected_count} selected. Confirm to {requestedAction}{' '}
            their Chapter belonging.
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!chapterId || pending}
            onClick={() => submit(Boolean(preview))}
          >
            {pending ? 'Working…' : preview ? `${verb} Chapter` : 'Preview change'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

async function previewRequest(
  chapterId: string,
  action: BulkChapterAction,
  selection: BulkChapterSelection,
) {
  try {
    return await request(chapterId, action, selection, false)
  } catch (error) {
    if (error instanceof ApiError && error.status === 409)
      return error.body as BulkChapterChangeResponse
    throw error
  }
}
function request(
  chapterId: string,
  action: BulkChapterAction,
  selection: BulkChapterSelection,
  confirm: boolean,
) {
  return patchJSON<BulkChapterChangeResponse>(`/api/v1/contacts/bulk_chapters/${chapterId}`, {
    bulk_chapter_change: { action, ...selection, confirm },
  })
}
